'use client';
import React, { memo, useEffect, useState } from "react";
import stl from "./Details.module.scss";
import { getProductDetail, getProductPackage } from "@/src/models/products";
import { PackageInfoProvider, ProductDetailsProvider, useCommonContext, usePageInfoContext, useRateContext } from "@/src/provider";
import Price from "./Price";
import { getActiveColorObj, getColorList, init } from "@/store/modules/productDetails";
import { cloneDeepWith } from "lodash";
import { calcStar } from "@/store/modules/reviewAndFq";
import classnames from "classnames";
import ProductImgSwiper from "./ProductImgSwiper";
import ProductDetailsColorSelect from "./ProductDetailsColorSelect";
import ButtonLine from "@/src/productDetails/ButtonLine";
import { ProductPackageProvider } from "@/src/provider";
import TryOnSkeleton from "@/src/components/CompTryOn/TryOnSkeleton";

interface DetailsProps {
    products_id: number;
}

interface ProductData {
    productDetails: any;
    activeObj: {
        activeObj: any;
        video: any;
    };
    colorsList: any[];
    productDetailsInitProps: any;
    packageInfoData: any;
    packageId: any;
    advertising: any;
}

const Details: React.FC<DetailsProps> = ({ products_id }) => {
    const [data, setData] = useState<ProductData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const { lang } = useCommonContext();

    useEffect(() => {
        const fetchData = async () => {
            if (!products_id) return;

            setLoading(true);

            try {
                const res = await getProductDetail(products_id);
                if (!res) {
                    return;
                }

                const colorRelations = res.products_color_relation || [];
                if (colorRelations.length === 0) {
                    return;
                }

                const initialColorId = colorRelations[0].color_id;
                const colorList = getColorList(colorRelations);
                const details:any = init(res);
                const activeColorObj = getActiveColorObj(
                    initialColorId,
                    cloneDeepWith(colorList),
                    details
                );

                const packageInfoData = null;
                const packageId =  null;

                setData({
                    productDetails: details,
                    activeObj: activeColorObj,
                    colorsList: colorList,
                    productDetailsInitProps: {
                        ...details,
                        colorId: details.products_color_relation[0].color_id,
                        packageId,
                    },
                    packageInfoData,
                    packageId,
                    advertising: null, // Initialize as needed
                });
            } catch (err) {
                console.error("Fetch error:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [products_id]);

    if (loading || !data) {
        return <TryOnSkeleton />;
    }

    return <DetailsContent {...data} lang={lang} />;
};

interface DetailsContentProps extends Omit<ProductData, 'packageInfoData' | 'packageId'> {
    lang: string;
    packageInfoData?: any;
    packageId?: any;
}

const DetailsContent: React.FC<DetailsContentProps> = ({
    productDetails,
    activeObj,
    colorsList,
    productDetailsInitProps,
    advertising,
    lang,
    packageInfoData,
    packageId,
}) => {
    const  stopPropagation = (e: React.TouchEvent) => {
        return e.stopPropagation();
    }

    return (
        <div className={stl["show-details-container"]} onTouchMove={(e) => {
                stopPropagation(e)
            }}
>
            <ProductDetailsProvider {...productDetailsInitProps}>
                <ProductImgSwiper
                    video={activeObj.video}
                    productDetail={productDetails}
                    activeObj={activeObj.activeObj}
                />
                <div className={classnames(stl["price-container"], "default_layout")}>
                    <div className={stl["price-content"]}>
                        <Price
                            activeObj={activeObj.activeObj}
                            productDetail={productDetails}
                            advertising={advertising}
                            starNum={calcStar(productDetails.products_avg_rating)}
                            commentNum={productDetails.products_reviews}
                        />
                    </div>
                </div>
                <div className={stl["page-item"]}>
                    <ProductDetailsColorSelect
                        isUkUser={productDetails?.isUkUser ?? false}
                        colorList={colorsList}
                        language={lang}
                        productDetail={productDetails}
                        activeObj={activeObj.activeObj}
                    />
                </div>
                <PackageInfoProvider {...{ ...packageInfoData, packageId }}>
                    <ProductPackageProvider>
                        <ButtonLine pageId={null} />
                    </ProductPackageProvider>
                </PackageInfoProvider>
            </ProductDetailsProvider>
        </div>
    );
};

export default memo(Details);